/* eslint-disable camelcase */
import * as Sentry from '@sentry/browser';
import { html, LitElement, nothing } from 'lit';
import { buildURL, fetchRequest } from 'Utils/api-fetch-request';
import { pluralize } from 'Utils/text';
import { isMobile } from 'Utils/viewport';

import Styles from './vm-video-comments.scss';

export default class VmVideoComments extends LitElement {
    static styles = Styles;

    static PAGINATION_LIMIT = 20;

    static properties = {
        dataLoginUrl: { type: String, attribute: 'data-login-url' },
        dataVideoSlug: { type: String, attribute: 'data-video-slug' },
        dataDisableComments: { type: Boolean, attribute: 'data-disable-comments' },
        showAllButtonVisible: { type: Boolean },
        featuredCommentsData: { state: true },
        userData: { state: true },
        commentsData: { state: true },
        pageIndex: { state: true },
        pagination: { state: true },
        dataFetched: { state: true },
    };

    constructor() {
        super();
        this.pageIndex = 1;
        this.userData = null;
        this.dataFetched = false;
        this.showAllButtonVisible = false;
        this.dataDisableComments = false;
    }

    _handleInputChange = () => {
        if (this._inputField?.value.trim() === '') {
            this._saveButton?.setAttribute('disabled', 'true');
        } else {
            this._saveButton?.removeAttribute('disabled');
        }
    };

    formFieldValidation() {
        this._inputField = this.querySelector('.js_commentBox');
        this._saveButton = this.querySelector('.js_saveNewComment');
        this._saveButton?.setAttribute('disabled', 'true');
        this._inputField?.addEventListener('input', this._handleInputChange);
    }

    resetForm() {
        // reset the form after successful submission
        // also close the attachment field if open
        const form = this.querySelector('.js_newCommentForm');
        if (form) {
            form.reset();
            this.querySelector('.js_commentAttachmentField .uk-close-column a')?.click();
        }
    }

    saveNewComment = (e) => {
        e?.preventDefault();

        this._saveButton?.setAttribute('disabled', 'true');

        const commentForm = this.querySelector('.js_newCommentForm');
        const url = `/api/videos/${this.dataVideoSlug}/comments/`;
        const formData = new FormData(commentForm);
        const headersOverride = { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' };

        if (formData.has('file')) {
            const files = formData.getAll('file');

            files.forEach((value) => {
                formData.append('attachment_ids', value);
            });

            formData.delete('file');
        }

        // overriding headers because fetchRequest by default sets Content-Type to application/json
        // but here we need multipart/form-data for file upload
        fetchRequest(url, { method: 'POST', body: formData, headers: headersOverride })
            .then(async () => {
                await this.fetchCommentsData();
                this.resetForm();
            })
            .catch((error) => {
                Sentry.captureException(error);
                this._saveButton?.removeAttribute('disabled');
            });
    };

    scrollToTop() {
        const componentYPosition = this.getBoundingClientRect().top + window.scrollY;
        const videoHeight = isMobile() ? (document.querySelector('video-js')?.offsetHeight ?? 0) : 0;

        const scrollPosition = componentYPosition - videoHeight;
        window.scrollTo({
            top: scrollPosition,
            behavior: 'smooth',
        });
    }

    handlePaginationClick(e) {
        this.pageIndex = e.detail.pageIndex;
        this.fetchCommentsData();
        this.scrollToTop();
    }

    handleShowAllCommentsClick() {
        this.dispatchEvent(
            new CustomEvent('show-all-comments', {
                bubbles: true,
                composed: true,
            }),
        );
    }

    async fetchUserPermissions() {
        try {
            const { user } = await fetchRequest('/api/me');
            this.userData = user;
        } catch {
            // userData remains null, login card will be shown
        }
    }

    filterFeaturedComments(comments) {
        return comments.reduce((acc, comment) => {
            const filteredChildren = comment.children ? this.filterFeaturedComments(comment.children) : [];

            if (comment.featured || filteredChildren.length > 0) {
                acc.push({
                    ...comment,
                    children: filteredChildren,
                });
            }

            return acc;
        }, []);
    }

    countFeaturedComments(comments) {
        return comments.reduce((count, comment) => {
            const childCount = comment.children ? this.countFeaturedComments(comment.children) : 0;
            return count + (comment.featured ? 1 : 0) + childCount;
        }, 0);
    }

    refetchComments() {
        this.fetchFeaturedCommentsData();
        this.fetchCommentsData();
    }

    async fetchFeaturedCommentsData() {
        const params = {
            limit: 500,
        };

        const url = buildURL(`/api/videos/${this.dataVideoSlug}/comments/`, params);

        try {
            const { results } = await fetchRequest(url);
            this.featuredCommentsData = this.filterFeaturedComments(results);
        } catch (error) {
            Sentry.captureException(error);
        }
    }

    async fetchCommentsData() {
        const params = {
            limit: this.constructor.PAGINATION_LIMIT,
            offset: (this.pageIndex - 1) * this.constructor.PAGINATION_LIMIT,
        };

        const url = buildURL(`/api/videos/${this.dataVideoSlug}/comments/`, params);

        try {
            const { count, results, total_count } = await fetchRequest(url);
            const totalPages = Math.ceil(count / this.constructor.PAGINATION_LIMIT);

            this.commentsData = { items: results, totalComments: total_count };

            this.pagination = {
                count,
                totalPages,
            };
        } catch (error) {
            Sentry.captureException(error);
        }
    }

    async connectedCallback() {
        super.connectedCallback();

        this.formFieldValidation();

        await this.fetchUserPermissions();

        if (!this.userData?.permissions?.view_comments) {
            this.dataFetched = true;
            return;
        }

        await Promise.allSettled([this.fetchFeaturedCommentsData(), this.fetchCommentsData()]);

        this.dataFetched = true;

        this._saveButton?.addEventListener('click', this.saveNewComment);
    }

    disconnectedCallback() {
        this._inputField?.removeEventListener('input', this._handleInputChange);
        this._saveButton?.removeEventListener('click', this.saveNewComment);
        super.disconnectedCallback();
    }

    render() {
        // Loading / initial state
        if (!this.dataFetched) {
            return html`
                <div class="comments">
                    <vm-loading-spinner size="3"></vm-loading-spinner>
                </div>
            `;
        }

        // Comments explicitly disabled
        if (this.dataDisableComments) {
            return html`
                <div class="comments">
                    <span class="comments__title"> Comments are disabled for this content. </span>
                </div>
            `;
        }

        return html`
            <div class="comments">
                <p class="comments__title">
                    Comments
                    <span class="comments__title-divider"></span>
                    ${this.commentsData?.totalComments ?? 0}
                </p>

                ${this.userData?.permissions?.add_comment ? html`<slot name="form-markup"></slot>` : nothing}
                ${this.userData?.permissions?.view_comments
                    ? html`
                          <div class="comments__wrapper js_commentsWrapper">
                              ${this.featuredCommentsData?.length
                                  ? (() => {
                                        const count = this.countFeaturedComments(this.featuredCommentsData);
                                        return html`
                                            <span class="comments__title">
                                                ${count} Featured comment${pluralize(count)}
                                            </span>
                                            <vm-video-comments-list
                                                .commentsData=${this.featuredCommentsData}
                                                .dataAddCommentsPerm=${this.userData?.permissions?.add_comment}
                                                .dataMarkFeaturedPerm=${this.userData?.permissions?.feature_comment}
                                                .videoSlug=${this.dataVideoSlug}
                                                @comment-updated=${() => this.refetchComments()}
                                            ></vm-video-comments-list>
                                            <span class="comments__title">All comments</span>
                                        `;
                                    })()
                                  : nothing}
                              <vm-video-comments-list
                                  .commentsData=${this.commentsData?.items ?? []}
                                  .dataAddCommentsPerm=${this.userData?.permissions?.add_comment}
                                  .dataMarkFeaturedPerm=${this.userData?.permissions?.feature_comment}
                                  .videoSlug=${this.dataVideoSlug}
                                  @comment-updated=${() => this.refetchComments()}
                              ></vm-video-comments-list>
                              ${this.pagination?.totalPages > 1
                                  ? html`
                                        <vm-list-pagination
                                            .totalPages=${this.pagination.totalPages}
                                            .currentPage=${this.pageIndex}
                                            @vm-pagination-click=${(e) => this.handlePaginationClick(e)}
                                        ></vm-list-pagination>
                                    `
                                  : nothing}
                          </div>
                          <button
                              class="vm-button vm-button--comment-cta vm-button--show-all-comments js_commentsShowAllButton ${this
                                  .showAllButtonVisible
                                  ? 'd-flex'
                                  : 'hidden'}"
                              @click=${this.handleShowAllCommentsClick}
                          >
                              Show All Comments
                          </button>
                      `
                    : !this.userData
                      ? html`
                            <div class="comments__login-card">
                                <div class="comments__login-card-text">
                                    Login to view comments.
                                    <a href="${this.dataLoginUrl}" title="Click here to Login"> Click here to Login </a>
                                </div>
                            </div>
                        `
                      : nothing}
            </div>
        `;
    }
}
